"use client";

import { useState, useEffect, useMemo, type ReactNode } from "react";
import type { Platform } from "@/payload-types";
import { siteConfig } from "@/lib/site.config";
import { PlatformCarousel } from "./PlatformCarousel";
import { PlatformContent } from "./PlatformContent";

interface PlatformsShowcaseProps {
  platforms: Platform[];
  initialSlug: string;
  missionGrids: Record<string, ReactNode>;
}

export function PlatformsShowcase({
  platforms,
  initialSlug,
  missionGrids,
}: PlatformsShowcaseProps) {
  const landingPath = "/platforms";
  const genericTitle = `Platforms | ${siteConfig.name}`;

  const initialIndex = useMemo(() => {
    const idx = platforms.findIndex((p) => p.slug === initialSlug);
    return idx >= 0 ? idx : 0;
  }, [platforms, initialSlug]);

  const [activeIndex, setActiveIndex] = useState(initialIndex);
  const [hasInteracted, setHasInteracted] = useState(false);
  const activePlatform = platforms[activeIndex];

  // Update URL and document title when active platform changes
  useEffect(() => {
    if (!activePlatform) return;

    const newPath = `/platforms/${activePlatform.slug}`;
    const isLandingState = !hasInteracted && window.location.pathname === landingPath;

    if (!isLandingState && window.location.pathname !== newPath) {
      window.history.replaceState(null, "", newPath);
    }

    document.title = isLandingState
      ? genericTitle
      : `${activePlatform.name} | Platforms`;
  }, [activePlatform, genericTitle, hasInteracted, landingPath]);

  const handleSelect = (index: number) => {
    setHasInteracted(true);
    setActiveIndex(index);
  };

  if (!activePlatform) return null;

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Platform Carousel Selector */}
      <PlatformCarousel
        platforms={platforms}
        activeIndex={activeIndex}
        onSelect={handleSelect}
      />

      {/* Per-Platform Content */}
      <PlatformContent platform={activePlatform} missionGrid={missionGrids[activePlatform.id]} />
    </div>
  );
}
